import { computeOnce } from 'utils/Func';

export interface FirebaseSdkConfig {
    projectId: string;
    appId: string;
    databaseURL?: string;
    storageBucket: string;
    locationId?: string;
    apiKey: string;
    authDomain: string;
    messagingSenderId: string;
}

export interface AdHubClientConfig {
    apiPrefix: string;
    assetsStoragePath: string;
    hostingBucket: string;
    scopes: ReadonlyArray<string>;
}

// TODO: is zod feasible in the browser?
function jsonParseObject(jsonText: string): Record<string, any> {
    const p = JSON.parse(jsonText);
    if (!p || typeof p !== 'object') {
        throw new Error('JSON.parse resulted in non-object value');
    }
    return p;
}

function optionalString(
    obj: Record<string, any>,
    key: string
): string | undefined {
    const value = obj[key];
    if (value == null) return undefined;
    if (typeof value === 'string') return value;
    throw new Error(
        `Value for ${JSON.stringify(key)} is not a string (${typeof value})`
    );
}

function requireString(obj: Record<string, any>, key: string): string {
    const value = optionalString(obj, key);
    if (value == null) {
        throw new Error(
            `Value for ${JSON.stringify(key)} is ${value === null ? 'null' : 'undefined'}`
        );
    }
    return value;
}

function requireNonEmptyString(obj: Record<string, any>, key: string): string {
    const value = requireString(obj, key);
    if (value.length === 0) {
        throw new Error(`Value for ${JSON.stringify(key)} has zero length`);
    }
    return value;
}

// webpack.DefinePlugin is configured so that every occurrence of
// process.env is substituted with an object literal, so assign it once
// here, then use the variable name.

const processEnv = process.env;

// All the config data passed in by the build can be found stored against a
// single env key. It's stored as a string containing JSON text, so needs to
// be JSON.parse()d.

const getRuntimeConfig = computeOnce(() => {
    const runtimeText = requireNonEmptyString(
        processEnv,
        'PUBLIC_ADHUB_RUNTIME'
    );
    try {
        return jsonParseObject(runtimeText);
    } catch (parseError) {
        console.warn(
            'Could not parse env.PUBLIC_ADHUB_RUNTIME: %o',
            runtimeText
        );
        throw parseError;
    }
});

// This is the same Firebase web SDK configuration that is output by
// `firebase apps:sdkconfig` and consumed by `firebase.initializeApp()`.
// In AdHub's case, however, it is fetched by Terraform so it can be shared
// to both the client and server.

export const getFirebaseSdkConfig = computeOnce((): FirebaseSdkConfig => {
    const rc = getRuntimeConfig();
    const mandatory = {
        projectId: requireNonEmptyString(rc, 'ADHUB_FB_PROJECT_ID'),
        appId: requireNonEmptyString(rc, 'ADHUB_FB_APP_ID'),
        storageBucket: requireNonEmptyString(rc, 'ADHUB_FB_STORAGE_BUCKET'),
        apiKey: requireNonEmptyString(rc, 'ADHUB_FB_API_KEY'),
        authDomain: requireNonEmptyString(rc, 'ADHUB_FB_AUTH_DOMAIN'),
        messagingSenderId: requireNonEmptyString(
            rc,
            'ADHUB_FB_MESSAGING_SENDER_ID'
        ),
    };

    const locationId = optionalString(rc, 'ADHUB_FB_LOCATION_ID');
    const locationIdPart = locationId ? { locationId } : {};

    return {
        ...mandatory,
        ...locationIdPart,
    };
});

// added in 60d5832; this is hardcoded; should it be externally configured
// like the rest?
const assetsStoragePath = 'hostedstorage/creative2/assets/';

export const getAdHubClientConfig = computeOnce((): AdHubClientConfig => {
    const rc = getRuntimeConfig();

    let apiPrefix = requireString(rc, 'ADHUB_API_PREFIX');
    if (apiPrefix && !apiPrefix.endsWith('/')) {
        apiPrefix += '/';
    }

    const scopesString = optionalString(rc, 'ADHUB_SCOPES');
    const scopes = scopesString ? scopesString.split(',') : [];

    const hostingBucket = requireNonEmptyString(
        rc,
        'ADHUB_USER_HOSTING_BUCKET'
    );

    return {
        apiPrefix,
        assetsStoragePath,
        hostingBucket,
        scopes,
    };
});

// Use of local emulators are configured separately through their own
// environment vars. If emulators are to be used, these variables will be
// set to a string 'host:port' nominating the emulator host. `host` will
// pretty much always be 'localhost'.

const emulatorIdToEnvVar: Array<[string, string]> = [
    ['auth', 'PUBLIC_FIREBASE_AUTH_EMULATOR_HOST'],
    ['database', 'PUBLIC_FIREBASE_DATABASE_EMULATOR_HOST'],
    ['extensions', 'PUBLIC_EXTENSIONS_EMULATOR_HOST'],
    ['firestore', 'PUBLIC_FIRESTORE_EMULATOR_HOST'],
    ['functions', 'PUBLIC_FUNCTIONS_EMULATOR_HOST'],
    ['hosting', 'PUBLIC_HOSTING_EMULATOR_HOST'],
    ['hub', 'PUBLIC_FIREBASE_EMULATOR_HUB'],
    ['logging', 'PUBLIC_LOGGING_EMULATOR_HOST'],
    ['pubsub', 'PUBLIC_PUBSUB_EMULATOR_HOST'],
    ['storage', 'PUBLIC_FIREBASE_STORAGE_EMULATOR_HOST'],
    ['ui', 'PUBLIC_UI_EMULATOR_HOST'],
];

export const getEmulatorConfig = computeOnce((): Record<string, string> => {
    return Object.fromEntries(
        emulatorIdToEnvVar
            .map(([emuId, envVar]) => {
                const value = optionalString(processEnv, envVar) || '';
                return [emuId, value] as const;
            })
            .filter(([_, value]) => value.length > 0)
    );
});
